import { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { ApplicationFormProvider, useApplicationForm } from "@/context/ApplicationFormContext";
import { useAuth } from "@/context/AuthContext";
import { FormStepper } from "@/components/application/FormStepper";
import {
  StepRecipient,
  StepBuilding,
  StepEquipment,
  StepDocuments,
  StepReview,
} from "@/components/application/steps";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { submitApplicationWithLog } from "@/lib/api/applications";

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
    </svg>
  );
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
    </svg>
  );
}

function PenIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
    </svg>
  );
}

function CloudIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
    </svg>
  );
}

function CheckCloudIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

// Save status indicator component with animation
function SaveIndicator() {
  const { isSaving, lastSaved, userSeq } = useApplicationForm();
  const [justSaved, setJustSaved] = useState(false);
  const prevLastSaved = useRef<Date | null>(null);
  
  // Detect when save completes and show "just saved" animation
  useEffect(() => {
    if (lastSaved && lastSaved !== prevLastSaved.current) {
      prevLastSaved.current = lastSaved;
      setJustSaved(true);
      const timer = setTimeout(() => setJustSaved(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [lastSaved]);
  
  if (isSaving) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-medium animate-pulse">
        <Spinner size="sm" />
        <span>Збереження...</span>
      </div>
    );
  }
  
  if (justSaved && lastSaved) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-300 text-green-700 text-[11px] font-medium transition-all duration-300">
        <CheckCloudIcon className="w-4 h-4" />
        <span>Збережено!</span>
      </div>
    );
  }
  
  if (lastSaved) {
    const timeAgo = formatTimeAgo(lastSaved);
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-slate-600 text-[11px] font-medium">
        <CloudIcon className="w-3.5 h-3.5" />
        <span>Збережено {timeAgo}</span>
      </div>
    );
  }
  
  if (userSeq) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-slate-500 text-[11px] font-medium">
        <CloudIcon className="w-3.5 h-3.5" />
        <span>Чернетка №{userSeq}</span>
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-slate-400 text-[11px] font-medium">
      <CloudIcon className="w-3.5 h-3.5" />
      <span>Нова заявка</span>
    </div>
  );
}

function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  if (seconds < 60) return "щойно";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} хв тому`;
  return `${Math.floor(seconds / 3600)} год тому`;
}

const STEP_TITLES = [
  "Інформація про отримувача",
  "Інформація про будинок",
  "Обладнання",
  "Документи",
  "Перевірка та підписання",
];

function WizardContent() {
  const { currentStep, setCurrentStep, totalSteps, canProceed, isLoading, applicationId, userSeq, saveDraft } = useApplicationForm();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSubmit = async () => {
    if (!applicationId) {
      alert("Спочатку збережіть заявку");
      return;
    }

    // For now, submit without КЕП signing (КЕП will be implemented later)
    const confirmSubmit = window.confirm(
      "Ви впевнені, що хочете подати заявку на розгляд?\n\n" +
      "Примітка: Функція підписання КЕП буде реалізована пізніше."
    );
    
    if (!confirmSubmit) return;

    setIsSubmitting(true);
    try {
      // Save any pending changes first
      await saveDraft();
      
      // Submit application with logging (records status history and audit log)
      const result = await submitApplicationWithLog(applicationId, user?.id);
      
      if (result) {
        alert("Заявку успішно подано на розгляд!");
        navigate("/applications");
      } else {
        alert("Помилка при поданні заявки. Спробуйте ще раз.");
      }
    } catch (error) {
      console.error("Failed to submit application:", error);
      alert("Помилка при поданні заявки. Спробуйте ще раз.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = canProceed(currentStep);

  // Validation is always enforced - no bypass even in dev mode
  const canGoNext = isStepValid;

  const completedSteps = Array.from({ length: currentStep - 1 }, (_, i) => i + 1);

  // Show loading state when loading existing application
  if (isLoading) {
    return (
      <AppLayout userRole="applicant">
        <div className="w-full flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center justify-center">
            <Spinner size="lg" />
            <p className="mt-4 text-muted-foreground">Завантаження заявки...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout userRole="applicant">
      <div className="space-y-6">
        {/* Back link */}
        <div className="flex items-center justify-between">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            На головну
          </Link>
          <SaveIndicator />
        </div>

        {/* Header */}
        <div className="-mt-2">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold tracking-tight">
              {applicationId ? "Редагування заявки" : "Нова заявка"}
            </h1>
            {userSeq && (
              <span className="px-2.5 py-1 text-[11px] font-bold bg-primary/10 text-primary rounded-lg">
                №{userSeq}
              </span>
            )}
          </div>
          <p className="text-[13px] text-muted-foreground mt-0.5">
            {STEP_TITLES[currentStep - 1]}
          </p>
        </div>

        {/* Stepper */}
        <div className="bg-white rounded-2xl border-[3px] border-border/50 p-5">
          <FormStepper
            currentStep={currentStep}
            completedSteps={completedSteps}
            onStepClick={(step) => {
              if (step < currentStep) {
                setCurrentStep(step);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          />
        </div>

        {/* Form content */}
        <div className="bg-white rounded-2xl border-[3px] border-border/50 p-6">
          {currentStep === 1 && <StepRecipient />}
          {currentStep === 2 && <StepBuilding />}
          {currentStep === 3 && <StepEquipment />}
          {currentStep === 4 && <StepDocuments />}
          {currentStep === 5 && <StepReview onGoToStep={setCurrentStep} />}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <div>
            {currentStep > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={handlePrev}
                className="gap-2"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Назад
              </Button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {!isStepValid && currentStep < 5 && (
              <p className="text-[12px] text-muted-foreground">
                Заповніть всі обов'язкові поля
              </p>
            )}

            {currentStep < totalSteps ? (
              <Button
                type="button"
                onClick={handleNext}
                disabled={!canGoNext}
                className={cn("gap-2", !canGoNext && "opacity-50")}
              >
                Далі
                <ArrowRightIcon className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={!canProceed(5) || isSubmitting}
                className={cn("gap-2", (!canProceed(5) || isSubmitting) && "opacity-50")}
              >
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" />
                    Подання...
                  </>
                ) : (
                  <>
                    <PenIcon className="h-4 w-4" />
                    Підписати та подати
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

export function ApplicationWizard() {
  const { id } = useParams<{ id?: string }>();
  
  return (
    <ApplicationFormProvider initialApplicationId={id}>
      <WizardContent />
    </ApplicationFormProvider>
  );
}

export default ApplicationWizard;
